import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import {
  addFraternityTag,
  addRusheeTag,
  deleteRushee as deleteRusheeApi,
  getAllEvents,
  getAllRushees,
  getFraternity,
  removeFraternityTag,
} from '../../utils/api';
import { getBrotherData } from '../../utils/auth';
import { Button, Card, MutedText, Row, Stack } from '../ui';
import Rushee from './Rushee';
import CSVExport from './CSVExport';

const Page = styled(Stack)`max-width: 1240px; margin: 0 auto;`;
const Toolbar = styled(Card)`padding: 14px;`;
const Search = styled.input`
  flex: 1; min-width: 220px; min-height: 38px; border: 1px solid ${({theme}) => theme.colors.borderStrong};
  border-radius: ${({theme}) => theme.radius.sm}; padding: 8px 11px; background: ${({theme}) => theme.colors.surface};
`;
const Select = styled.select`
  min-height: 38px; border: 1px solid ${({theme}) => theme.colors.borderStrong};
  border-radius: ${({theme}) => theme.radius.sm}; padding: 7px 30px 7px 10px; background: ${({theme}) => theme.colors.surface};
`;
const FilterPanel = styled.details`
  border-top: 1px solid ${({theme}) => theme.colors.border}; padding-top: 12px;
  summary { cursor: pointer; color: ${({theme}) => theme.colors.textMuted}; font-weight: 600; }
`;
const EventGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 8px; margin-top: 12px;
  label { display: flex; align-items: center; gap: 8px; }
`;
const Tag = styled.span`
  display: inline-flex; align-items: center; gap: 6px; padding: 4px 8px; border-radius: 999px;
  color: ${({theme}) => theme.colors.accent}; background: ${({theme}) => theme.colors.accentSoft}; font-size: 12px;
  button { border: 0; background: transparent; color: inherit; cursor: pointer; padding: 0; }
`;
const Avatar = styled.img`
  width: 100%; aspect-ratio: 4 / 3; object-fit: cover; background: ${({theme}) => theme.colors.surfaceMuted};
`;
const Name = styled.div`font-weight: 650;`;
const Email = styled.div`color: ${({theme}) => theme.colors.textMuted}; font-size: 12px; overflow: hidden; text-overflow: ellipsis;`;
const Status = styled.span`
  display: inline-flex; padding: 4px 8px; border-radius: 999px; background: ${({theme}) => theme.colors.surfaceMuted}; font-size: 12px;
`;
const PeopleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 14px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;
const PersonCard = styled(Card)`
  cursor: pointer;
  overflow: hidden;
  padding: 0;
  transition: border-color .18s ease, box-shadow .18s ease, transform .18s ease;

  &:hover {
    border-color: ${({theme}) => theme.colors.borderStrong};
    box-shadow: 0 10px 28px rgba(32, 32, 30, .09);
    transform: translateY(-2px);
  }
`;
const PhotoWrap = styled.div`
  position: relative;
  background: ${({theme}) => theme.colors.surfaceMuted};
`;
const SelectPerson = styled.input`
  position: absolute;
  right: 12px;
  top: 12px;
  width: 18px;
  height: 18px;
  accent-color: ${({theme}) => theme.colors.accent};
`;
const CardBody = styled.div`
  padding: 14px;
`;
const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
`;
const EventCount = styled.span`
  color: ${({theme}) => theme.colors.textMuted};
  font-size: 12px;
  white-space: nowrap;
`;
const Empty = styled(Card)`text-align: center; color: ${({theme}) => theme.colors.textMuted};`;
const DangerButton = styled(Button)`
  background: ${({theme}) => theme.colors.danger}; border-color: ${({theme}) => theme.colors.danger};
  &:disabled { opacity: .45; cursor: not-allowed; }
`;
const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 40; background: rgba(32,32,30,.32); backdrop-filter: blur(2px);
`;
const Drawer = styled.aside`
  position: fixed; z-index: 41; top: 0; right: 0; width: min(720px, 92vw); height: 100vh;
  overflow: auto; background: ${({theme}) => theme.colors.canvas}; box-shadow: -12px 0 36px rgba(32,32,30,.16);
  @media (max-width: 720px) { width: 100vw; }
`;
const DrawerBar = styled.div`
  position: sticky; top: 0; z-index: 2; display: flex; align-items: center; justify-content: space-between;
  padding: 12px 16px; background: rgba(247,247,244,.94); border-bottom: 1px solid ${({theme}) => theme.colors.border};
  backdrop-filter: blur(10px);
`;
const Close = styled.button`border: 0; background: transparent; cursor: pointer; font-size: 22px;`;
const Meta = styled.span`color: ${({theme}) => theme.colors.textMuted}; font-size: 12px;`;

const DisplayRushees = () => {
  const brother = getBrotherData();
  const isPresident = brother?.position === 'President';
  const canManageTags = isPresident || brother?.position === 'Rush Chair';
  const navigate = useNavigate();
  const { rusheeId } = useParams();
  const [rushees, setRushees] = useState([]);
  const [events, setEvents] = useState([]);
  const [tags, setTags] = useState([]);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [tag, setTag] = useState('');
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [sort, setSort] = useState('recent');
  const [selected, setSelected] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [bulkTag, setBulkTag] = useState('');

  const loadRushees = useCallback(async () => {
    try {
      const response = await getAllRushees({ fraternity: brother.frat });
      setRushees(response.data.data || []);
    } catch (error) { console.error('Failed to fetch rushees:', error); }
  }, [brother.frat]);

  useEffect(() => {
    loadRushees();
    getAllEvents({ fraternity: brother.frat }).then(r => setEvents(r.data || [])).catch(e => console.error('Failed to fetch events:', e));
    getFraternity(brother.frat).then(r => setTags(r.data.tags || [])).catch(e => console.error('Failed to fetch tags:', e));
  }, [brother.frat, loadRushees]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const result = rushees.filter(r =>
      (!needle || `${r.name} ${r.email}`.toLowerCase().includes(needle)) &&
      (!status || r.status === status) &&
      (!tag || (r.tags || []).includes(tag)) &&
      selectedEvents.every(id => (r.eventsAttended || []).some(event => event._id === id))
    );
    return sort === 'alpha' ? [...result].sort((a, b) => (a.name || '').localeCompare(b.name || '')) : result;
  }, [query, rushees, selectedEvents, sort, status, tag]);

  const open = id => navigate(`/app/rushees/${id}`);
  const close = () => navigate('/app/rushees');
  const toggleEvent = id => setSelectedEvents(current => current.includes(id) ? current.filter(x => x !== id) : [...current, id]);
  const togglePerson = (event, id) => {
    event.stopPropagation();
    setSelected(current => current.includes(id) ? current.filter(x => x !== id) : [...current, id]);
  };
  const addTag = async () => {
    const value = newTag.trim();
    if (!value || tags.includes(value)) return;
    await addFraternityTag(brother.frat, value);
    setTags(current => [...current, value]);
    setNewTag('');
  };
  const removeTag = async value => {
    await removeFraternityTag(brother.frat, value);
    setTags(current => current.filter(item => item !== value));
  };
  const applyBulkTag = async () => {
    if (!bulkTag) return;
    await Promise.all(filtered.map(r => addRusheeTag(r._id, bulkTag)));
    await loadRushees();
  };
  const deleteSelected = async () => {
    if (!selected.length || !window.confirm(`Delete ${selected.length} selected rushee${selected.length === 1 ? '' : 's'}? This cannot be undone.`)) return;
    await Promise.all(selected.map(id => deleteRusheeApi(id, brother.frat)));
    if (selected.includes(rusheeId)) close();
    setSelected([]);
    await loadRushees();
  };

  return (
    <Page $gap="md">
      <Toolbar>
        <Stack $gap="md">
          <Row $wrap>
            <Search aria-label="Search rushees" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search people by name or email…" />
            <Select value={status} onChange={e => setStatus(e.target.value)} aria-label="Filter by status">
              <option value="">All statuses</option><option>Potential</option><option>Active</option><option>Dropped</option><option>Rejected</option>
            </Select>
            <Select value={tag} onChange={e => setTag(e.target.value)} aria-label="Filter by tag">
              <option value="">All tags</option>{tags.map(value => <option key={value}>{value}</option>)}
            </Select>
            <Select value={sort} onChange={e => setSort(e.target.value)} aria-label="Sort people">
              <option value="recent">Recently added</option><option value="alpha">Name A–Z</option>
            </Select>
            <CSVExport rushees={filtered} allRushees={rushees} />
          </Row>
          <FilterPanel>
            <summary>Events attended {selectedEvents.length ? `· ${selectedEvents.length} selected` : ''}</summary>
            <EventGrid>{events.map(event => <label key={event._id}><input type="checkbox" checked={selectedEvents.includes(event._id)} onChange={() => toggleEvent(event._id)} />{event.name}</label>)}</EventGrid>
          </FilterPanel>
          {canManageTags && (
            <FilterPanel open>
              <summary>Tag management</summary>
              <Stack $gap="sm" style={{marginTop: 12}}>
                <Row $wrap>{tags.map(value => <Tag key={value}>{value}<button onClick={() => removeTag(value)} aria-label={`Remove ${value}`}>×</button></Tag>)}</Row>
                <Row $wrap>
                  <Search value={newTag} onChange={e => setNewTag(e.target.value)} placeholder="Create a tag" />
                  <Button onClick={addTag}>Create tag</Button>
                  <Select value={bulkTag} onChange={e => setBulkTag(e.target.value)}><option value="">Tag current view…</option>{tags.map(value => <option key={value}>{value}</option>)}</Select>
                  <Button $variant="secondary" disabled={!bulkTag} onClick={applyBulkTag}>Apply to {filtered.length}</Button>
                </Row>
              </Stack>
            </FilterPanel>
          )}
        </Stack>
      </Toolbar>

      <Row style={{justifyContent: 'space-between'}}>
        <Meta>{filtered.length} of {rushees.length} people</Meta>
        {isPresident && <DangerButton disabled={!selected.length} onClick={deleteSelected}>Delete selected {selected.length ? `(${selected.length})` : ''}</DangerButton>}
      </Row>

      {filtered.length ? (
        <PeopleGrid>{filtered.map(r => (
          <PersonCard key={r._id} onClick={() => open(r._id)}>
            <PhotoWrap>
              <Avatar
                src={r.picture || '/default.jpg'}
                alt=""
                onError={e => { e.currentTarget.src = '/default.jpg'; }}
              />
              {isPresident && (
                <SelectPerson
                  aria-label={`Select ${r.name}`}
                  type="checkbox"
                  checked={selected.includes(r._id)}
                  onChange={e => togglePerson(e, r._id)}
                  onClick={e => e.stopPropagation()}
                />
              )}
            </PhotoWrap>
            <CardBody>
              <CardHeader>
                <div><Name>{r.name}</Name><Email>{r.email}</Email></div>
                <EventCount>{(r.eventsAttended || []).length} events</EventCount>
              </CardHeader>
              <Row $wrap $gap="xs" style={{marginTop: 12}}>
                <Status>{r.status || 'Potential'}</Status>
                {(r.tags || []).slice(0, 3).map(value => <Tag key={value}>{value}</Tag>)}
              </Row>
            </CardBody>
          </PersonCard>
        ))}</PeopleGrid>
      ) : <Empty><Name>No people match this view</Name><MutedText>Try clearing one or more filters.</MutedText></Empty>}

      {rusheeId && <><Overlay onClick={close} /><Drawer role="dialog" aria-modal="true" aria-label="Rushee details">
        <DrawerBar><Meta>Person details</Meta><Close onClick={close} aria-label="Close details">×</Close></DrawerBar>
        <Rushee rusheeId={rusheeId} />
      </Drawer></>}
    </Page>
  );
};

export default DisplayRushees;
